// Loads the Learning Center library for the current rooftop: resolves the live version per
// key and scopes by audience (dealer, plus driver if the user is also a driver), tier, and
// enabled products (from effective_config). The list query omits the markdown `content` body;
// the reader fetches it per key (see useLearningModule).

import { useMemo, useState } from 'react';
import { useGetLearningModulesQuery } from '../queries/getLearningModules.query.generated';
import { resolveLiveModules, filterForRooftop } from '../utils/resolveModules';
import { useDealerScope } from './useDealerScope';
import { featureLabel, type ModuleSummary } from '../types';

export function useLearningCenter() {
  const { scope, ready: scopeReady } = useDealerScope();
  const { data, loading, error, refetch } = useGetLearningModulesQuery({ fetchPolicy: 'cache-and-network' });
  const [search, setSearch] = useState('');

  const allModules = useMemo<ModuleSummary[]>(
    () => (data?.learning_modules ?? []) as unknown as ModuleSummary[],
    [data],
  );

  // Live, in-scope modules for this rooftop — the rooftop's full catalog, independent of search.
  // Consumers that describe the catalog (progress meter, new-content alert) must use this list,
  // not the search-filtered one, or typing in the search box would change what they report.
  const scopedModules = useMemo<ModuleSummary[]>(() => {
    if (!scope) return [];
    return filterForRooftop(resolveLiveModules(allModules, new Date()), scope);
  }, [allModules, scope]);

  // The scoped catalog narrowed by the search box. Matches the category as displayed
  // (featureLabel) — users search what they see on screen, not the raw enum key.
  const modules = useMemo<ModuleSummary[]>(() => {
    if (!search.trim()) return scopedModules;
    const q = search.toLowerCase().trim();
    return scopedModules.filter(
      m =>
        m.name.toLowerCase().includes(q) ||
        (m.description?.toLowerCase().includes(q) ?? false) ||
        featureLabel(m.feature).toLowerCase().includes(q) ||
        m.feature.toLowerCase().includes(q),
    );
  }, [scopedModules, search]);

  // Scope hydration is part of "loading": until the rooftop lands, an empty list means
  // "don't know yet", and the page must show skeletons — not "No training available yet."
  return { modules, scopedModules, loading: loading || !scopeReady, error, refetch, search, setSearch };
}
